import { IconCheck, IconCircleDashed, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { ChecklistState } from "../use-home-data";

const DISMISS_KEY = "home_setup_dismissed";

type SetupChecklistProps = {
  businessType: "restaurant" | "supermarket";
  state: ChecklistState;
};

type Item = {
  key: keyof Omit<ChecklistState, "allComplete">;
  to: string;
  labelKey: string;
};

const ITEMS: Item[] = [
  { key: "hasLogo", to: "/organizations", labelKey: "dashboard.checklist.logo" },
  {
    key: "hasSection",
    to: "/menu/sections/new",
    labelKey: "dashboard.checklist.section",
  },
  {
    key: "hasProduct",
    to: "/menu/products/new",
    labelKey: "dashboard.checklist.product",
  },
  {
    key: "hasTeam",
    to: "/organizations",
    labelKey: "dashboard.checklist.invite",
  },
  {
    key: "hasSubscription",
    to: "/billing",
    labelKey: "dashboard.checklist.subscription",
  },
];

export function SetupChecklist({ businessType, state }: SetupChecklistProps) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const isSupermarket = businessType === "supermarket";

  useEffect(() => {
    setDismissed(localStorage.getItem(DISMISS_KEY) === "true");
  }, []);

  if (state.allComplete || dismissed) {
    return null;
  }

  const completed = ITEMS.filter((item) => state[item.key]).length;
  const total = ITEMS.length;

  return (
    <div className="px-4 lg:px-6">
      <Card className="@container/card bg-gradient-to-t from-primary/5 to-card shadow-xs">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle>{t("dashboard.checklist.title")}</CardTitle>
              <CardDescription>
                {t("dashboard.checklist.description", {
                  completed,
                  total,
                })}
              </CardDescription>
            </div>
            <Button
              aria-label={t("dashboard.checklist.dismiss")}
              onClick={() => {
                localStorage.setItem(DISMISS_KEY, "true");
                setDismissed(true);
              }}
              size="icon"
              variant="ghost"
            >
              <IconX className="size-4" />
            </Button>
          </div>
        </CardHeader>
        <ul className="flex flex-col gap-1 px-6 pb-6">
          {ITEMS.map((item) => {
            const done = state[item.key];
            return (
              <li key={item.key}>
                <Link
                  className={cn(
                    "flex items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors",
                    done ? "text-muted-foreground" : "hover:bg-muted/50",
                  )}
                  to={item.to}
                >
                  {done ? (
                    <IconCheck className="size-4 shrink-0 text-primary" />
                  ) : (
                    <IconCircleDashed className="size-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className={cn(done && "line-through")}>
                    {item.key === "hasSection" && isSupermarket
                      ? t("dashboard.checklist.category", {
                          defaultValue: "Crea tu primera categoría del catálogo",
                        })
                      : t(item.labelKey)}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
}
